import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Phone } from "lucide-react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import { AddTransferPhoneNumberSchema } from "utils/validationSchema";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";

interface AddHandlerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refreshProfileInfo: () => void;
}

function AddHandlerDialog({
  open,
  onOpenChange,
  refreshProfileInfo,
}: AddHandlerDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddTransferPhoneNumberSchema),
    mode: "onSubmit", // Only validate on submit
    reValidateMode: "onChange",
    defaultValues: {
      transfer_to_rep_name: "",
      transfer_to_rep_number: "",
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      reset(); // Reset form when dialog opens
    }
  }, [open, reset]);

  const onSubmit = async (data: any) => {
    console.log("Handler form data being submitted:", data); // Debug log
    setIsSubmitting(true);
    try {
      await baseApi.post("/dashboard/add_transfer_phone_number", data, {
        withCredentials: true,
      });
      Mixpanel.track("New transfer phone number added");
      refreshProfileInfo();
      onOpenChange(false);
      toast.success("Handler added successfully!");
      reset(); // Reset form after successful submission
    } catch (error: any) {
      console.log("Handler submission error:", error);
      Mixpanel.track("Error: New transfer phone number added", {
        error: error,
      });
      const message = error.response?.data?.detail || error.response?.data;
      toast.error(message || "Failed to add handler");
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    reset(); // Reset form when closing
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Phone className="h-5 w-5 text-[#005151]" />
            <span>Add Call Handler</span>
          </DialogTitle>
          <DialogDescription>
            Add a staff member who can handle transferred calls. Our AI will
            transfer calls to them when needed.
          </DialogDescription>
        </DialogHeader>

        <form
          key={`handler-form-${open}`}
          onSubmit={handleSubmit(onSubmit, (errors) => {
            console.log("Handler form validation errors:", errors);
          })}
          className="space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Name</label>
            <Input
              placeholder="Staff member's name"
              {...register("transfer_to_rep_name")}
            />
            {errors.transfer_to_rep_name && (
              <p className="text-sm text-red-600">
                {errors.transfer_to_rep_name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <Input
              placeholder="10 digits (no dashes)"
              {...register("transfer_to_rep_number")}
            />
            {errors.transfer_to_rep_number && (
              <p className="text-sm text-red-600">
                {errors.transfer_to_rep_number.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                "Add Handler"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddHandlerDialog;
